import { defineStore } from 'pinia';
import { computed } from 'vue';

import { stepConfigs } from '@bv/components/workflow/partials/steps';

import { getClonedFieldsForNodeType } from '../utils/node-validation';

import { useWorkflowStore } from './workflow';
import { useWorkflowGraph } from './workflow-graph';
import { useWorkflowSelection } from './workflow-selection';

import type { CopiedNodePayload, FlowNode } from '../types/workflow';

// Constants
const ELSE_CONNECTOR_NAME = 'else';

type CreationResult = {
  rootId: string | number;
  leafNodeIds: Array<string | number>;
  firstConnectorId: string | number | null;
  createdNodeIds: Array<string | number>;
};

export const useWorkflowClipboard = defineStore('bvWorkflowClipboard', () => {
  const graphStore = useWorkflowGraph();
  const store = useWorkflowStore();
  const selectionStore = useWorkflowSelection();

  const getBaseStepTitle = (stepType: string, fallbackName?: string) => {
    return (
      stepConfigs[stepType]?.title ||
      (fallbackName
        ? fallbackName
            .replace(/\s+#\d+\s*(?:-\s*copy)?$/i, '')
            .replace(/\s+-\s*copy$/i, '')
            .trim()
        : '') ||
      stepType
    );
  };

  const getStepCountsByType = (excludeNodeIds?: Array<string | number>) => {
    const excludeSet = excludeNodeIds ? new Set(excludeNodeIds) : null;
    return Object.values(graphStore.flow).reduce(
      (counts, node) => {
        // Exclude nodes that are being pasted (not yet renamed)
        if (node.category !== 'Connector' && (!excludeSet || !excludeSet.has(node.id))) {
          counts[node.type] = (counts[node.type] ?? 0) + 1;
        }
        return counts;
      },
      {} as Record<string, number>,
    );
  };

  const processPastedNodes = (nodeIds: Array<string | number>, typeCounts: Record<string, number>) => {
    nodeIds.forEach((id) => {
      const node = graphStore.flow[id];
      if (!node || node.category === 'Connector') {
        return;
      }

      const nextNumber = (typeCounts[node.type] ?? 0) + 1;
      typeCounts[node.type] = nextNumber;
      node.name = `${getBaseStepTitle(node.type, node.name)} #${nextNumber} - copy`;

      const clonedFields = getClonedFieldsForNodeType(node.type);
      if (clonedFields) {
        store.validateNode(id, clonedFields, node.data || {});
      }
    });
  };

  const syncBranchRepeaterConnectors = (nodeIds: Array<string | number>) => {
    nodeIds.forEach((id) => {
      const node = graphStore.flow[id];
      const branches = (node?.data?.branches as any[]) ?? [];
      if (node?.category !== 'Router' || !branches.length) {
        return;
      }

      const connectors = (node.children ?? [])
        .map((childId) => graphStore.flow[childId])
        .filter((child): child is FlowNode => child?.category === 'Connector' && child.name !== ELSE_CONNECTOR_NAME);

      branches.forEach((branch: any) => {
        const connector = connectors.find((c) => c.name === branch.name);
        if (connector) {
          branch.connectorId = String(connector.id);
        }
      });
    });
  };

  const copyNode = (nodeId: string | number) => {
    const node = graphStore.flow[nodeId];
    if (!node) {
      return;
    }

    const snapshot = graphStore.buildNodeSnapshot(nodeId);
    selectionStore.setCopiedNode({
      snapshot,
      branchRootId: graphStore.getBranchRootId(nodeId),
      sourceNodeId: nodeId,
      hasSubSteps: snapshot.children.length > 0,
      totalSteps: graphStore.countSnapshotSteps(snapshot),
    });
  };

  // Finds the leaf node in the pasted tree to attach original children.
  const findPastedTreeLeaf = (
    pastedRootId: string | number,
    creationResult: CreationResult,
  ): string | number | null => {
    const pastedRootNode = graphStore.flow[pastedRootId];
    if (!pastedRootNode) {
      return null;
    }

    // If the pasted node is a Router, use the first connector's leaf
    if (pastedRootNode.category === 'Router' && creationResult.firstConnectorId) {
      return graphStore.findLeafNodeInBranch(creationResult.firstConnectorId);
    }

    // For regular nodes, pick the best continuation node from the pasted tree
    return graphStore.pickContinuationNode(creationResult.leafNodeIds, pastedRootId);
  };

  // Inserts the pasted node between parent and its original children.
  const connectPastedNode = (
    parentNode: FlowNode,
    parentNodeId: string | number,
    pastedRootId: string | number,
    originalChildren: Array<string | number>,
    creationResult: CreationResult,
  ) => {
    const pastedRootNode = graphStore.flow[pastedRootId];
    if (!pastedRootNode) {
      return;
    }

    // Connect pasted root to parent
    parentNode.children = [pastedRootId];
    pastedRootNode.parentId = parentNodeId;

    // Attach original children to the leaf of the pasted tree
    if (originalChildren.length > 0) {
      // Find the appropriate leaf node in the pasted tree
      const pastedLeaf = findPastedTreeLeaf(pastedRootId, creationResult);

      if (pastedLeaf) {
        store.addToHistory(pastedLeaf);
        graphStore.attachExistingChildren(originalChildren, pastedLeaf);
      }
    }
  };

  const pasteNode = (
    payload: CopiedNodePayload | null,
    parentNodeId: string | number,
    includeSubtree: boolean = false,
  ): string | number | null => {
    if (!payload || !parentNodeId) {
      return null;
    }

    const parentNode = graphStore.flow[parentNodeId];
    if (!parentNode) {
      return null;
    }

    const stepsToAdd = includeSubtree ? payload.totalSteps : 1;
    if (payload.snapshot.category !== 'Connector' && graphStore.totalSteps + stepsToAdd > graphStore.MAX_STEPS) {
      return null;
    }

    store.startHistory();
    store.addToHistory(parentNodeId);

    const originalChildren = [...parentNode.children];
    const creationResult = graphStore.instantiateSnapshot(
      payload.snapshot,
      parentNodeId,
      includeSubtree ? 'full' : 'single',
    ) as CreationResult;

    creationResult.createdNodeIds.forEach((id) => store.addToHistory(id, true));

    // Get step counts excluding the newly created nodes to get correct numbering
    processPastedNodes(creationResult.createdNodeIds, getStepCountsByType(creationResult.createdNodeIds));
    syncBranchRepeaterConnectors(creationResult.createdNodeIds);

    if (originalChildren.length > 0) {
      connectPastedNode(parentNode, parentNodeId, creationResult.rootId, originalChildren, creationResult);
    } else {
      parentNode.children = [creationResult.rootId];
      graphStore.flow[creationResult.rootId].parentId = parentNodeId;
    }

    store.endHistory();
    return creationResult.rootId;
  };

  const targetBranchRootId = computed(() =>
    selectionStore.addClickedNode ? graphStore.getBranchRootId(selectionStore.addClickedNode.id) : null,
  );

  const findParentRouter = (connectorId: string | number | null): string | number | null => {
    const connector = graphStore.flow[connectorId ?? ''];
    return connector?.category === 'Connector' ? connector.parentId : null;
  };

  const isSameBranchPaste = computed(() => {
    const sourceId = selectionStore.copiedNode?.branchRootId;
    const targetId = targetBranchRootId.value;
    if (!sourceId || !targetId) {
      return false;
    }
    if (sourceId === targetId) {
      return true;
    }
    const sourceRouter = findParentRouter(sourceId);
    const targetRouter = findParentRouter(targetId);
    // Same Router but different connectors = different branches (allow pasting)
    if (sourceRouter && targetRouter && sourceRouter === targetRouter) {
      return false;
    }
    // Different Routers or one/both are not connectors - allow pasting
    return false;
  });

  const hasCopiedNode = computed(() => Boolean(selectionStore.copiedNode));
  const hasAddTarget = computed(() => Boolean(selectionStore.addClickedNode));
  const isAtMaxSteps = computed(() => graphStore.totalSteps >= graphStore.MAX_STEPS);

  const pasteSingleReason = computed(() => {
    if (!hasCopiedNode.value) {
      return 'No copied step available';
    }
    if (!hasAddTarget.value) {
      return 'Select a branch to paste';
    }
    if (isAtMaxSteps.value) {
      return 'Max steps limit reached';
    }
    return '';
  });

  const pasteSubtreeReason = computed(() => {
    if (!hasCopiedNode.value) {
      return 'No copied step available';
    }
    if (!selectionStore.copiedNode?.hasSubSteps) {
      return 'Copied step has no sub-steps';
    }
    if (!hasAddTarget.value) {
      return 'Select a branch to paste';
    }
    if (isSameBranchPaste.value) {
      return 'Cannot paste sub-steps in the same branch';
    }
    if (graphStore.totalSteps + (selectionStore.copiedNode?.totalSteps || 0) > graphStore.MAX_STEPS) {
      return 'Pasting would exceed the max steps limit';
    }
    return '';
  });

  const canPasteSingleStep = computed(() => pasteSingleReason.value === '');
  const canPasteStepWithChildren = computed(() => pasteSubtreeReason.value === '');

  const pasteStepOptions = computed(() => {
    const hasSubsteps = Boolean(selectionStore.copiedNode?.hasSubSteps);
    return {
      single: {
        type: 'PasteStep',
        canShow: hasCopiedNode.value,
        canUse: canPasteSingleStep.value,
        reason: pasteSingleReason.value,
      },
      subtree: {
        type: 'PasteStepWithSubsteps',
        canShow: hasCopiedNode.value && hasSubsteps,
        canUse: canPasteStepWithChildren.value,
        reason: pasteSubtreeReason.value,
      },
    };
  });

  const pasteSingle = () =>
    canPasteSingleStep.value && selectionStore.copiedNode && selectionStore.addClickedNode
      ? pasteNode(selectionStore.copiedNode, selectionStore.addClickedNode.id, false)
      : null;

  const pasteWithSubtree = () =>
    canPasteStepWithChildren.value && selectionStore.copiedNode && selectionStore.addClickedNode
      ? pasteNode(selectionStore.copiedNode, selectionStore.addClickedNode.id, true)
      : null;

  return {
    targetBranchRootId,
    isSameBranchPaste,
    pasteSingleReason,
    pasteSubtreeReason,
    canPasteSingleStep,
    canPasteStepWithChildren,
    pasteStepOptions,
    copyNode,
    pasteSingle,
    pasteWithSubtree,
  };
});
